import { COLLECTION_PATHS, SUBSCRIPTION_PATHS } from './StorageDriver.js';

const VALID_PATHS = new Set(SUBSCRIPTION_PATHS);

// Conservative live set for the app shell and common gameplay surfaces.
// Session 3 intentionally keeps core gameplay and messaging live while moving
// high-cost optional histories/projections behind route activation.
export const BASE_STORAGE_SUBSCRIPTION_PATHS = [
  'chars',
  'food',
  'diets',
  'diseases',
  'foodTypes',
  'locations',
  'globalSettings',
  'spellLibrary',
  'groupTravelLog',
  'currentLocation',
  'journalSettings',
  'combat',
  'auditSessionMeta',
  'partyInventory',
];

const PATH_FEATURE_KEYS = {
  combat: 'combat',
  combatSnapshots: 'combat',
  systems: 'systems',
  shopRuntime: 'shops',
  lootRuntime: 'loot',
  groupTravelLog: 'travelLog',
};

const PATH_HOMEBREW_KEYS = {
  food: 'foodSettings',
  foodTypes: 'foodSettings',
  diets: 'diets',
  diseases: 'diseases',
};

const pathAllowedByFeatureAccess = (path, featureAccess) => {
  const featureKey = PATH_FEATURE_KEYS[path];
  if (!featureKey || !featureAccess || typeof featureAccess !== 'object') return true;
  return featureAccess[featureKey] !== false;
};

const pathAllowedByHomebrewAccess = (path, homebrewAccess) => {
  const homebrewKey = PATH_HOMEBREW_KEYS[path];
  if (!homebrewKey || !homebrewAccess || typeof homebrewAccess !== 'object') return true;
  return homebrewAccess[homebrewKey] !== false;
};

const addAllowedPath = (paths, path, featureAccess, homebrewAccess) => {
  if (
    pathAllowedByFeatureAccess(path, featureAccess)
    && pathAllowedByHomebrewAccess(path, homebrewAccess)
  ) paths.add(path);
};

export const PLAYER_BASE_STORAGE_SUBSCRIPTION_PATHS = [
  'messageLiveTail',
];

export const DM_BASE_STORAGE_SUBSCRIPTION_PATHS = [
];

export const ROUTE_STORAGE_SUBSCRIPTION_PATHS = {
  home: ['systems'],
  travel: ['systems'],
  combat: ['combatSnapshots', 'systems'],
  timeline: ['auditMeta', 'auditRecent'],
  journal: ['systems'],
  'bug-reports': ['auditRecent', 'systems'],
  'dev-dashboard': ['auditRecent', 'systems'],
  'dev-reports': ['auditRecent', 'systems'],
  shop: ['shopRuntime', 'systems'],
  loot: ['lootRuntime', 'systems'],
  crafting: ['systems'],
  npcs: ['systems'],
  dm: ['systems', 'shopRuntime', 'lootRuntime'],
};

export const DM_ROUTE_STORAGE_SUBSCRIPTION_PATHS = {
  home: ['npcLibrary'],
  travel: ['npcLibrary'],
  combat: ['npcLibrary'],
  dm: ['npcLibrary'],
  // Library still needs legacy journal arrays for compatibility until the UI
  // consumes row paths directly. Keep these route-scoped instead of baseline.
  npcs: ['npcLibrary', 'journalRecent', 'dmJournalRecent', 'journal', 'dmJournal'],
};

export const OPEN_SURFACE_STORAGE_SUBSCRIPTION_PATHS = {
  playerMessage: ['messagePanelHistory', 'messageLiveTail'],
  dmMessage: ['messagePanelHistory'],
  // Full JournalPage still needs the compatibility array until search/counts
  // and parser bases have row-backed equivalents.
  playerJournal: ['journalRecent', 'journalPage', 'journal'],
  // Floating Quick Journal is intentionally row-light. Extra discovery uses
  // imperative row-backed queries, not live full-array subscriptions.
  playerQuickJournal: ['journalRecent', 'journalPage'],
  // DM Library still needs full player/DM arrays for cross-linking and reveal
  // flows. Keep it separate from the floating quick journal surface.
  dmJournal: ['npcLibrary', 'dmJournalRecent', 'dmJournalPage', 'dmJournal'],
  dmQuickJournal: ['npcLibrary', 'dmJournalRecent', 'dmJournalPage'],
};

export const normalizeStorageSubscriptionPaths = (paths) => {
  const source = Array.isArray(paths) && paths.length > 0 ? paths : COLLECTION_PATHS;
  const result = [];
  const seen = new Set();
  for (const path of source) {
    if (!VALID_PATHS.has(path) || seen.has(path)) continue;
    seen.add(path);
    result.push(path);
  }
  return result;
};

export const buildStorageSubscriptionPaths = ({
  view,
  isDM,
  isMessageSurfaceOpen = false,
  isPlayerJournalSurfaceOpen = false,
  isPlayerQuickJournalSurfaceOpen = false,
  isDmJournalSurfaceOpen = false,
  isDmQuickJournalSurfaceOpen = false,
  featureAccess = null,
  homebrewAccess = null,
} = {}) => {
  const paths = new Set();
  BASE_STORAGE_SUBSCRIPTION_PATHS.forEach(path => addAllowedPath(paths, path, featureAccess, homebrewAccess));
  if (isDM) {
    DM_BASE_STORAGE_SUBSCRIPTION_PATHS.forEach(path => addAllowedPath(paths, path, featureAccess, homebrewAccess));
  } else {
    PLAYER_BASE_STORAGE_SUBSCRIPTION_PATHS.forEach(path => addAllowedPath(paths, path, featureAccess, homebrewAccess));
  }
  (ROUTE_STORAGE_SUBSCRIPTION_PATHS[view] || []).forEach(path => addAllowedPath(paths, path, featureAccess, homebrewAccess));
  if (isDM) {
    (DM_ROUTE_STORAGE_SUBSCRIPTION_PATHS[view] || []).forEach(path => addAllowedPath(paths, path, featureAccess, homebrewAccess));
  }
  if (isMessageSurfaceOpen) {
    const messagePaths = isDM
      ? OPEN_SURFACE_STORAGE_SUBSCRIPTION_PATHS.dmMessage
      : OPEN_SURFACE_STORAGE_SUBSCRIPTION_PATHS.playerMessage;
    messagePaths.forEach(path => addAllowedPath(paths, path, featureAccess, homebrewAccess));
  }
  if (isPlayerJournalSurfaceOpen) {
    OPEN_SURFACE_STORAGE_SUBSCRIPTION_PATHS.playerJournal.forEach(path => addAllowedPath(paths, path, featureAccess, homebrewAccess));
  }
  if (isPlayerQuickJournalSurfaceOpen) {
    OPEN_SURFACE_STORAGE_SUBSCRIPTION_PATHS.playerQuickJournal.forEach(path => addAllowedPath(paths, path, featureAccess, homebrewAccess));
  }
  if (isDmJournalSurfaceOpen) {
    OPEN_SURFACE_STORAGE_SUBSCRIPTION_PATHS.dmJournal.forEach(path => addAllowedPath(paths, path, featureAccess, homebrewAccess));
  }
  if (isDmQuickJournalSurfaceOpen) {
    OPEN_SURFACE_STORAGE_SUBSCRIPTION_PATHS.dmQuickJournal.forEach(path => addAllowedPath(paths, path, featureAccess, homebrewAccess));
  }
  return normalizeStorageSubscriptionPaths([...paths]);
};
